import axiosInstance from "@/utils/https";
import { ApiRequestClient } from "@/utils/api-client";
import { OrdersApiResponse } from "@/types/Order.types";
import { ApiCategory, MenuItem } from "@/types/Menu.types";

// Add new interface definitions for API payloads
interface ConfirmOrderPayload {
  status?: string;
  note?: string;
}

interface EditOrderPayload {
  address?: string;
  note?: string;
  scheduledTime?: string;
  orderType?: string;
  totalAmount?: number;
}

interface CancelOrderPayload {
  reason?: string;
  note?: string;
}

interface AddAddressPayload {
  address: string;
  latitude?: number;
  longitude?: number;
  isDefault?: boolean;
  label?: string;
}

/**
 * Fetch user orders with authentication
 */
export const GetOrders = async (): Promise<OrdersApiResponse> => {
  try {
    // Get token from localStorage as fallback
    const token = localStorage.getItem("atk");

    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await ApiRequestClient.get("/order/me", null, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const UpdateOrderDetails = async (
  id: string,
  payload: {
    address?: string;
    note?: string;
    scheduledTime?: string;
    scheduledDate?: string;
  }
) => {
  try {
    const url = `api/v1/order/${id}`;
    const res = await axiosInstance.patch(url, payload);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const UpdateUserOrderDetails = async (orderid: string) => {
  try {
    const url = `api/v1/order/user/${orderid}`;
    await axiosInstance.patch(url);
    // return res.data;
  } catch (error) {
    throw error;
  }
};

export const GetOneOrder = async (id: string) => {
  try {
    const url = `api/v1/orders/${id}`;
    const response = await axiosInstance.get(url);

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch order data by ID with authentication
 * Used for Lola payment flow
 */
export const GetOrderData = async (orderId: string) => {
  try {
    // Get token from localStorage
    const token = localStorage.getItem("atk");

    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await ApiRequestClient.get(`/order/data/${orderId}`, null, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Refresh virtual account details
 */
export const RefreshVirtualAccount = async (payload: {
  entityId: string;
  entityType: string;
}) => {
  try {
    const token = localStorage.getItem("atk");
    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await ApiRequestClient.post(
      "api/v1/payment/virtual-account/refresh",
      payload,
      headers
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Subscribe to transaction updates via SSE
 * Uses fetchEventSource as Axios does not support SSE natively in browser
 */
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { baseUrl } from '@/utils/axios.instance';

export const SubscribeToTransactionUpdates = (
  orderId: string,
  onMessage: (data: any) => void,
  signal: AbortSignal
) => {
  const token = localStorage.getItem("atk");

  if (!token) return;

  const url = `${baseUrl}/push-events/transaction-notify`;

  fetchEventSource(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal: signal,
    onmessage(event) {
      // Prevent parsing empty data which causes SyntaxError
      if (!event.data) return;

      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (err) {
        console.error("Error parsing SSE message:", err);
      }
    },
    onerror(err) {
      console.error("SSE Error:", err);
    },
  }).catch((err) => console.error("SSE Setup Error:", err));
};

export const ConfirmOrders = async (
  id: string,
  payload: ConfirmOrderPayload
) => {
  try {
    const url = `api/v1/orders/order/confirm/${id}`;
    const response = await axiosInstance.patch(url, payload);

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const EditOrders = async (id: string, payload: EditOrderPayload) => {
  try {
    const url = `api/v1/orders/${id}`;
    const response = await axiosInstance.patch(url, payload);

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const CancelOrders = async (id: string, payload: CancelOrderPayload) => {
  try {
    const url = `api/v1/orders/${id}/cancel`;
    const response = await axiosInstance.patch(url, payload);

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetUserLatAndLong = async (id: string) => {
  try {
    const url = `api/v1/outlets/maps?place_id=${id}`;
    const response = await axiosInstance.get(url);

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetNearbyOutlets = async (lat: number, long: number) => {
  try {
    const url = `api/v1/outlets/nearby?lat=${lat}&long=${long}`;
    const response = await axiosInstance.get(url);

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetOutletsAroundMe = async (lat: number, long: number) => {
  try {
    const url = `api/v1/outlets?lat=${lat}&long=${long}`;
    const response = await axiosInstance.get(url);

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add new interface definitions for API responses
interface AddressAutocompleteItem {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface OutletInfo {
  _id: string;
  name: string;
  address: string;
  phone_number: string;
  image: string;
  latitude?: number;
  longitude?: number;
}

interface MenuApiResponse {
  status: string;
  message: string;
  data: {
    categories: ApiCategory[];
    menuItems: MenuItem[];
    outlet: OutletInfo;
  };
}

export const GetAddress = async (
  address: string
): Promise<{
  status: string;
  message: string;
  data: AddressAutocompleteItem[];
}> => {
  try {
    const url = `api/v1/outlets/map/autocomplete?input=${address}`;
    const res = await axiosInstance.get(url);

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const GetOutletMenu = async (id: string): Promise<MenuApiResponse> => {
  const url = `api/v1/menu/${id}`;
  const res = await axiosInstance.get(url);

  return res.data;
};

export const AddAddress = async (payload: AddAddressPayload) => {
  try {
    const url = "api/v1/users/address";
    const res = await axiosInstance.post(url, payload);

    return res.data;
  } catch (err) {
    throw err;
  }
};

export const DeleteAddress = async (id: string) => {
  try {
    const url = `api/v1/users/address/${id}`;
    const res = await axiosInstance.delete(url);

    return res.data;
  } catch (err) {
    throw err;
  }
};

export const GetMyAddresses = async () => {
  try {
    const url = "api/v1/users/address";
    const res = await axiosInstance.get(url);

    return res.data;
  } catch (err) {
    throw err;
  }
};
